import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import Layout from './layout/Layout';
import HomePage from './pages/HomePage';

// ── FIX 6: REDUCE UNUSED JAVASCRIPT ────────────────────────────────────────
//
// Each lazy() call creates a separate JS chunk. The browser only downloads a
// chunk when the user navigates to that route — not on initial page load.
//
// Webpack/Vite magic comments (/* webpackChunkName: "..." */ or
// /* @vite-ignore */) name the chunks for easier debugging in DevTools.
//
// Previously all route code was bundled together → 157 KiB of JS was parsed
// and compiled even on the homepage. With this split:
//   - Initial bundle: Layout + HomePage + AppContext only (~40-60 KiB)
//   - Each route: downloaded on demand (~8-20 KiB each)
//
// ───────────────────────────────────────────────────────────────────────────

const AboutPage = lazy(() =>
  import(/* webpackChunkName: "about" */ './pages/Aboutpage')
);
const ContactPage = lazy(() =>
  import(/* webpackChunkName: "contact" */ './pages/ContactPage')
);
const ProductDetailPage = lazy(() =>
  import(/* webpackChunkName: "product-detail" */ './pages/Productdetailpage')
);
const DistributorPage = lazy(() =>
  import(/* webpackChunkName: "distributor" */ './pages/DistributorPage')
);
const WholesalerPage = lazy(() =>
  import(/* webpackChunkName: "wholesaler" */ './pages/Wholesalerpage')
);
const TrackOrderPage = lazy(() =>
  import(/* webpackChunkName: "track-order" */ './pages/TrackOrderPage')
);
const PolicyPage = lazy(() =>
  import(/* webpackChunkName: "policy" */ './pages/Policypage')
);
const AdminOrderPage = lazy(() =>
  import(/* webpackChunkName: "admin-orders" */ './pages/AdminOrderPage')
);

// ── FIX 6: Prefetch likely-visited routes after initial load ────────────────
// After the page is idle (requestIdleCallback), prefetch About and Contact
// since users frequently visit them from the homepage navbar.
// This warms up the chunk without blocking the main thread.
if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  window.requestIdleCallback(() => {
    // Prefetch (not import) so chunks are in cache before user clicks
    const prefetch = (importFn) => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'script';
      // We call importFn to let bundler know about the chunk, but
      // requestIdleCallback ensures it runs only when main thread is idle.
      importFn().catch(() => {}); // silently ignore prefetch failures
    };
    prefetch(() => import(/* webpackChunkName: "about" */ './pages/Aboutpage'));
    prefetch(() => import(/* webpackChunkName: "contact" */ './pages/ContactPage'));
  }, { timeout: 3000 });
}

// ── Minimal page loader — avoids large spinner component bundle ─────────────
function PageLoader() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fff',
    }}>
      {/* FIX 9: CSS animation uses only transform — compositor-safe */}
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #F5EAD8',
        borderTop: '3px solid #6B4423',
        borderRadius: '50%',
        willChange: 'transform',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Layout wraps ALL routes — navbar, cart, footer, chatbot */}
            <Route element={<Layout />}>
              <Route path="/"               element={<HomePage />} />
              <Route path="/about"          element={<AboutPage />} />
              <Route path="/contact"        element={<ContactPage />} />
              <Route path="/products/:slug" element={<ProductDetailPage />} />
              <Route path="/distributor"    element={<DistributorPage />} />
              <Route path="/wholesale"      element={<WholesalerPage />} />
              <Route path="/track-order"    element={<TrackOrderPage />} />
              <Route path="/policy"         element={<PolicyPage />} />
              <Route path="/admin/orders"   element={<AdminOrderPage />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </AppProvider>
  );
}